import { Vue, Options } from 'vue-class-component';
import {
  ActionDTO,
  ConditionDTO,
  DateAndTimeTrigger,
  TimeCondition,
  TriggerDTO,
  WaitTimeAction,
} from '@/../client-generator/generated-clients/plants';
import { SceneUtils } from '@/core/utils/scene.utils';

interface IDisplayWeekDay {
  day: number;
  dayName: string;
  disabled?: boolean;
}

@Options({
  name: 'SceneTimeObject',
  props: {
    timeObject: {
      type: Object,
      required: true,
    },
  },
})
export default class SceneTimeObject extends Vue {
  timeObject!: any;

  get isDateAndTimeTrigger(): boolean {
    return this.polymorphicTimeObject instanceof DateAndTimeTrigger;
  }

  get isWaitTimeAction(): boolean {
    return this.polymorphicTimeObject instanceof WaitTimeAction;
  }

  get isTimeCondition(): boolean {
    return this.polymorphicTimeObject instanceof TimeCondition;
  }

  get waitTime(): string {
    if (!this.polymorphicTimeObject || !this.isWaitTimeAction) return '';
    let time: string;
    const splitWaitTime = (this
      .polymorphicTimeObject as WaitTimeAction).waitTime?.split(':');
    return `${splitWaitTime![1]} ${this.$t('AppResource.MinutesShort')} ${splitWaitTime![2]} ${this.$t('AppResource.SecondsShort')}`
  }

  get weekDays(): IDisplayWeekDay[] {
    if (!this.polymorphicTimeObject) return [];
    const days = [0, 1, 2, 3, 4, 5, 6];
    if (!(this.polymorphicTimeObject as DateAndTimeTrigger).weekDays) return [];
    const enabledWeekDays = (this.polymorphicTimeObject as DateAndTimeTrigger)
      .weekDays as number[];
    const finalWeekDays: IDisplayWeekDay[] = days.map((d) => {
      return {
        day: d,
        dayName: SceneUtils.getWeekDayShortName(d),
        disabled:
          enabledWeekDays.find((enabledDay) => enabledDay === d) === undefined,
      };
    });
    return finalWeekDays;
  }

  get timeCondition(): string {
    if (!this.polymorphicTimeObject || !this.isTimeCondition) return '';
    const fromTime = (this.polymorphicTimeObject as TimeCondition).startTime!.substr(0, 5)
    const toTime = (this.polymorphicTimeObject as TimeCondition).endTime!.substr(0, 5)
    return `${this.$t('AppResource.FromCapitalized')} ${fromTime} ${this.$t('AppResource.To')} ${toTime}`
  }

  get timeTrigger(): string {
    if (!this.polymorphicTimeObject) return '';
    return ((this.polymorphicTimeObject as DateAndTimeTrigger)
      .time as string).substr(0, 5);
  }

  get isForEveryday(): boolean {
    return this.weekDays.every((d) => !d.disabled);
  }

  get polymorphicTimeObject():
    | WaitTimeAction
    | DateAndTimeTrigger
    | TimeCondition
    | undefined {
    let polymorphicTimeObject;

    let params: any;
    if (this.timeObject instanceof ActionDTO) {
      polymorphicTimeObject = new WaitTimeAction();
      params = this.timeObject.actionParams;
      (polymorphicTimeObject as WaitTimeAction).id = params.id;
      (polymorphicTimeObject as WaitTimeAction).waitTime = params.waitTime;
    }

    if (this.timeObject instanceof TriggerDTO) {
      polymorphicTimeObject = new DateAndTimeTrigger();
      params = this.timeObject.triggerParams;
      (polymorphicTimeObject as DateAndTimeTrigger).id = params.id;
      (polymorphicTimeObject as DateAndTimeTrigger).time = params.time;
      (polymorphicTimeObject as DateAndTimeTrigger).weekDays = params.weekDays;
      (polymorphicTimeObject as DateAndTimeTrigger).isSuspended =
        params.isSuspended;
    }

    if (this.timeObject instanceof ConditionDTO) {
      polymorphicTimeObject = new TimeCondition();
      params = this.timeObject.conditionParams;
      (polymorphicTimeObject as TimeCondition).id = params.id;
      (polymorphicTimeObject as TimeCondition).endTime = params.endTime;
      (polymorphicTimeObject as TimeCondition).weekDays = params.weekDays;
      (polymorphicTimeObject as TimeCondition).startTime = params.startTime;
      (polymorphicTimeObject as TimeCondition).isSuspended = params.isSuspended;
    }
    return polymorphicTimeObject;
  }
}
